import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type {
	GridCustomCellProps,
	GridFilterCellProps,
} from "@tql/ui-components-react/kendo/grid";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { cornerDownRight } from "@tql/ui-components-react/untitled/icons";

import { CopyQuoteCell } from "~/shared/components/trax-grid/copy-quote-cell/copy-quote-cell.tsx";
import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { IdLinkCell } from "~/shared/components/trax-grid/id-link-cell/id-link-cell.tsx";
import { LoaderCell } from "~/shared/components/trax-grid/loader-cell/loader-cell.tsx";
import { OperatorTextFilterCell } from "~/shared/components/trax-grid/operator-text-filter-cell/operator-text-filter-cell.tsx";
import { QuoteStatusCell } from "~/shared/components/trax-grid/quote-status-cell/quote-status-cell.tsx";
import type { QuoteBoardItem } from "~/shared/schemas/quote-board-schema.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import { sendQuoteBoardEditQuoteIdEvent } from "~/shared/utils/google-analytics-helper.ts";

import { BulkTenderInsuranceCell } from "../quote-board-grid/components/bulk-tender-insurance-cell/bulk-tender-insurance-cell.tsx";
import {
	formatCurrency,
	formatDateString,
} from "../utils/quote-board-data-transformers.ts";
import {
	currencyContainsOperator,
	dateContainsOperator,
	weightContainsOperator,
} from "../utils/quote-board-filter-operators.ts";
import {
	getQuoteBoardCarrierAction,
	getQuoteBoardQuoteUrl,
} from "../utils/quote-board-status.ts";

// Declared at module scope: inline components would be a new type on every
// render, remounting the filter input and dropping focus mid-keystroke.
const DateFilterCell = (props: GridFilterCellProps) => (
	<OperatorTextFilterCell {...props} operator={dateContainsOperator} />
);

const CurrencyFilterCell = (props: GridFilterCellProps) => (
	<OperatorTextFilterCell {...props} operator={currencyContainsOperator} />
);

const WeightFilterCell = (props: GridFilterCellProps) => (
	<OperatorTextFilterCell {...props} operator={weightContainsOperator} />
);

// The external (CUSTOMERQUOTEBOARD) endpoint is server-scoped to quotes the
// caller is authorized to view and act on, so per-row ownership checks are
// intentionally omitted here. Every rendered row is openable and requoteable.
export const getQuoteBoardColumns = (
	isLoading: boolean,
	isInternal: boolean,
	onOpenCarrierSelection?: (quoteId: number) => void,
	onOpenInsurance?: (quoteId: number) => void,
	hasInsurance?: (quoteId: number) => boolean,
	onOpenBulkCarrierSelection?: (quoteId: number) => void,
	quoteType = "Individual",
	isBulkTenderAvailable = false,
): BoardColumn[] => {
	const isBulkQuoteView = quoteType === "Bulk";

	const columns: BoardColumn[] = [
		{
			field: "quoteId",
			title: "Quote ID",
			menu: true,
			show: true,
			width: 125,
			type: "number",
			cells: {
				data: (props: GridCustomCellProps) => {
					const dataItem = props.dataItem as QuoteBoardItem;
					const isBulk = dataItem.bulkQuoteId != null;
					const hasError = dataItem.bulkUploadException != null;
					const quoteUrl = getQuoteBoardQuoteUrl(dataItem, { isInternal });

					return isLoading ? (
						<LoaderCell {...props} />
					) : (
						<IdLinkCell
							displayText={
								<div className={"quote-id-cell-content"}>
									{isBulk ? (
										<SvgIcon
											className={"bulk-quote-icon"}
											color={"black"}
											icon={cornerDownRight}
											size={"medium"}
										/>
									) : null}
									{hasError ? "0000000" : String(dataItem.quoteId)}
								</div>
							}
							isDisabled={!quoteUrl}
							linkTo={quoteUrl}
							onClick={() =>
								sendQuoteBoardEditQuoteIdEvent(String(dataItem.quoteId))
							}
							{...props}
						/>
					);
				},
			},
		},
		{
			field: "source",
			title: "Source",
			menu: true,
			show: false,
			width: 120,
			hiddenFromCustomers: true,
		},
		{
			field: "customerName",
			title: "Customer Name",
			menu: true,
			show: true,
			width: 235,
			hiddenFromCustomers: true,
			minResizableWidth: 50,
		},
		{
			field: "shipmentDate",
			title: "Shipment Date",
			menu: true,
			show: false,
			width: 130,
			type: "date",
			sortable: true,
			filterCell: DateFilterCell,
			cells: {
				data: (props: GridCustomCellProps) => {
					const dateValue = (props.dataItem as QuoteBoardItem)?.shipmentDate;
					const formattedDate = formatDateString(dateValue) ?? "";
					return isLoading ? (
						<LoaderCell {...props} />
					) : (
						<GridTd tdProps={props.tdProps}>{formattedDate}</GridTd>
					);
				},
			},
		},
		{
			field: "poNumber",
			title: "PO #",
			menu: true,
			show: true,
			width: 100,
			type: "number",
			hiddenFromCustomers: true,
		},
		{
			field: "originAddress",
			title: "Origin",
			menu: true,
			show: true,
			width: 175,
		},
		{
			field: "destinationAddress",
			title: "Destination",
			menu: true,
			show: true,
			width: 175,
		},
		{
			field: "totalWeight",
			title: "Weight",
			menu: true,
			show: true,
			width: 85,
			type: "number",
			filterCell: WeightFilterCell,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? (
						<LoaderCell {...props} />
					) : (
						<GridTd tdProps={props.tdProps}>
							{`${(props.dataItem as QuoteBoardItem)?.totalWeight ?? 0} lbs`}
						</GridTd>
					),
			},
		},
		{
			field: "statusDisplay",
			title: "Status",
			menu: true,
			show: true,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? (
						<LoaderCell {...props} />
					) : (
						<QuoteStatusCell
							isInternal={isInternal}
							onOpenCarrierSelection={onOpenCarrierSelection}
							{...props}
						/>
					),
			},
			width: 150,
		},
		{
			field: "customerRate",
			title: "Rate",
			menu: true,
			show: true,
			width: 100,
			type: "number",
			filterCell: CurrencyFilterCell,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? (
						<LoaderCell {...props} />
					) : (
						<GridTd tdProps={props.tdProps}>
							{isBulkTenderAvailable &&
							(props.dataItem as QuoteBoardItem).selectedCarrierPriceId == null
								? "\u2014"
								: formatCurrency(
										(props.dataItem as QuoteBoardItem)?.customerRate ?? null,
									)}
						</GridTd>
					),
			},
		},
		{
			field: "carrierName",
			title: "Carrier Name",
			menu: true,
			show: true,
			width: 200,
			cells: {
				data: (props: GridCustomCellProps) => {
					const item = props.dataItem as QuoteBoardItem;
					const carrierAction = onOpenBulkCarrierSelection
						? getQuoteBoardCarrierAction(item, { isInternal })
						: "none";
					return isLoading ? (
						<LoaderCell {...props} />
					) : (
						<GridTd tdProps={props.tdProps}>
							<div className={"quote-board-carrier-cell"}>
								{item.carrierName ? <span>{item.carrierName}</span> : null}
								{carrierAction === "none" ? null : (
									<Button
										onClick={() => onOpenBulkCarrierSelection?.(item.quoteId)}
										variant={"secondary"}
									>
										{carrierAction === "change" ? "Change" : "Select"}
									</Button>
								)}
							</div>
						</GridTd>
					);
				},
			},
		},
		{
			field: "transitDays",
			title: "Transit Days",
			menu: true,
			show: false,
			width: 130,
			cells: {
				data: (props: GridCustomCellProps) => {
					const transitDays = (props.dataItem as QuoteBoardItem)?.transitDays;
					return isLoading ? (
						<LoaderCell {...props} />
					) : (
						<GridTd tdProps={props.tdProps}>{transitDays ?? ""}</GridTd>
					);
				},
			},
		},
		{
			field: "carrierRate",
			title: "Carrier Rate",
			menu: true,
			show: false,
			width: 120,
			type: "number",
			hiddenFromCustomers: true,
			filterCell: CurrencyFilterCell,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? (
						<LoaderCell {...props} />
					) : (
						<GridTd tdProps={props.tdProps}>
							{formatCurrency(
								(props.dataItem as QuoteBoardItem)?.carrierRate ?? null,
							)}
						</GridTd>
					),
			},
		},
		{
			field: "maxLiability",
			title:
				isBulkQuoteView && onOpenInsurance
					? "Max Liability (New / Used) | Insurance"
					: "Max Liability",
			menu: true,
			show: false,
			width: 260,
			cells: {
				data: (props: GridCustomCellProps) => {
					const item = props.dataItem as QuoteBoardItem;
					if (isLoading) return <LoaderCell {...props} />;
					return isBulkTenderAvailable ? (
						<BulkTenderInsuranceCell
							{...props}
							dataItem={item}
							hasInsurance={hasInsurance?.(item.quoteId)}
							onOpenInsurance={onOpenInsurance}
						/>
					) : (
						<GridTd tdProps={props.tdProps}>{item.maxLiability ?? null}</GridTd>
					);
				},
			},
		},
		{
			field: "margin",
			title: "Margin",
			menu: true,
			show: false,
			width: 175,
			type: "number",
			hiddenFromCustomers: true,
			filterCell: CurrencyFilterCell,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? (
						<LoaderCell {...props} />
					) : (
						<GridTd tdProps={props.tdProps}>
							{formatCurrency(
								(props.dataItem as QuoteBoardItem)?.margin ?? null,
							)}
						</GridTd>
					),
			},
		},
		{
			field: "createdByName",
			title: "Requested By",
			menu: true,
			show: true,
			width: 140,
		},
		{
			field: "ltlOwner",
			title: "LTL Owner",
			menu: true,
			show: true,
			width: 140,
			hiddenFromCustomers: true,
		},
		{
			field: "ftlOwner",
			title: "FTL Owner",
			menu: true,
			show: false,
			width: 175,
			hiddenFromCustomers: true,
		},
		{
			field: "createdDate",
			title: "Created Date",
			menu: true,
			show: false,
			width: 130,
			type: "date",
			filterCell: DateFilterCell,
			cells: {
				data: (props: GridCustomCellProps) => {
					const dateValue = (props.dataItem as QuoteBoardItem)?.createdDate;
					const formattedDate = formatDateString(dateValue) ?? "";
					return isLoading ? (
						<LoaderCell {...props} />
					) : (
						<GridTd tdProps={props.tdProps}>{formattedDate}</GridTd>
					);
				},
			},
			sortable: true,
		},
		{
			field: "customerReferences",
			title: "Customer Reference",
			menu: true,
			show: false,
			width: 180,
		},
		{
			field: "",
			title: "",
			show: true,
			menu: false,
			filterable: false,
			width: 50,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? <LoaderCell {...props} /> : <CopyQuoteCell {...props} />,
			},
		},
	];

	// Excluded entirely (not just hidden) so stale saved prefs can't reveal it.
	return isBulkTenderAvailable
		? columns
		: columns.filter((column) => column.field !== "transitDays");
};
